import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bot, AlertTriangle } from "lucide-react";

export default function AISettings() {
  const [b, setB] = useState(null);
  const [warnOpen, setWarnOpen] = useState(false);
  useEffect(() => { api.get("/business").then(r => setB(r.data)); }, []);
  if (!b) return <div className="p-8 text-slate-500">Loading…</div>;
  const ai = b.ai_config || {};
  const set = (k, v) => setB({ ...b, ai_config: { ...ai, [k]: v } });
  const doSave = async () => {
    try { await api.patch("/business", { ai_config: b.ai_config, onboarding: { ...b.onboarding, ai: true } });
      setWarnOpen(false);
      toast.success("Receptionist saved");
    } catch { toast.error("Save failed"); }
  };
  const save = () => {
    if (ai.transfer_enabled !== false && !(ai.transfer_number || "").trim()) { setWarnOpen(true); return; }
    doSave();
  };

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">AI Receptionist</h1>
      <p className="text-slate-600 mt-1">How ASFA sounds and behaves on calls.</p>

      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><Bot className="w-5 h-5 text-blue-600" /></div>
          <div>
            <div className="font-heading font-semibold text-lg">{ai.receptionist_name || "Ava"}</div>
            <div className="text-sm text-slate-500">{ai.tone}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Receptionist name</Label><Input data-testid="ai-name" value={ai.receptionist_name || ""} onChange={(e) => set("receptionist_name", e.target.value)} className="mt-1.5" /></div>
          <div>
            <Label>Transfer number (for human)</Label>
            <Input data-testid="ai-transfer" value={ai.transfer_number || ""} onChange={(e) => set("transfer_number", e.target.value)} className="mt-1.5" placeholder="e.g. +1 416 555 0100" />
            {!(ai.transfer_number || "").trim() && ai.transfer_enabled !== false && (
              <p data-testid="ai-transfer-hint" className="mt-1.5 text-xs text-amber-600">Recommended — urgent and emergency calls are transferred to this number live.</p>
            )}
          </div>
          <div className="sm:col-span-2"><Label>Greeting</Label><Input data-testid="ai-greeting" value={ai.greeting || ""} onChange={(e) => set("greeting", e.target.value)} className="mt-1.5" placeholder="Thanks for calling. How can I help?" /></div>
        </div>

        <div className="mt-6">
          <Label>How should your receptionist sound?</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="tone-presets">
            {["Friendly", "Professional", "Casual", "Warm", "Concise", "Premium", "Energetic", "Reassuring"].map(t => (
              <button key={t} data-testid={`tone-${t.toLowerCase()}`} onClick={() => set("tone", t.toLowerCase())}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${ai.tone === t.toLowerCase() ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-700 hover:border-blue-400"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Receptionist voice (ElevenLabs)</Label>
            <select data-testid="ai-voice" value={ai.elevenlabs_voice_id || "hpp4J3VqNfWAUOO0d1Us"} onChange={(e) => set("elevenlabs_voice_id", e.target.value)}
              className="mt-1.5 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white">
              <option value="hpp4J3VqNfWAUOO0d1Us">Bella — professional, bright, warm</option>
              <option value="EXAVITQu4vr4xnSDxMaL">Sarah — mature, reassuring, confident</option>
              <option value="cgSgspJ2msm6clMCkdW9">Jessica — playful, bright, warm</option>
              <option value="XrExE9yKIg1WjnnlVkGX">Matilda — knowledgeable, professional</option>
              <option value="pFZP5JQG7iQjIQuC4Bku">Lily — velvety, warm</option>
              <option value="Xb7hH8MSUJpSbSDYk0k2">Alice — clear, engaging</option>
            </select>
          </div>
          <div>
            <Label>Formality</Label>
            <select data-testid="ai-formality" value={ai.formality || "neutral"} onChange={(e) => set("formality", e.target.value)}
              className="mt-1.5 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white">
              <option value="casual">Casual</option>
              <option value="neutral">Neutral</option>
              <option value="formal">Formal</option>
            </select>
          </div>
          <div>
            <Label>Response length</Label>
            <select data-testid="ai-response-length" value={ai.response_length || "short"} onChange={(e) => set("response_length", e.target.value)}
              className="mt-1.5 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white">
              <option value="short">Short (1 sentence)</option>
              <option value="balanced">Balanced (1-2 sentences)</option>
            </select>
          </div>
          <div><Label>Preferred phrases</Label><Input data-testid="ai-preferred" value={ai.preferred_phrases || ""} onChange={(e) => set("preferred_phrases", e.target.value)} className="mt-1.5" placeholder="e.g. 'No problem', 'See you soon'" /></div>
          <div><Label>Words to avoid</Label><Input data-testid="ai-avoid" value={ai.avoid_phrases || ""} onChange={(e) => set("avoid_phrases", e.target.value)} className="mt-1.5" placeholder="e.g. 'buddy', corporate jargon" /></div>
          <div className="sm:col-span-2">
            <Label>Anything else your receptionist should know about how to communicate?</Label>
            <Textarea data-testid="ai-custom-instructions" rows={3} value={ai.custom_instructions || ""} onChange={(e) => set("custom_instructions", e.target.value)} className="mt-1.5"
              placeholder="Keep responses short and casual. Don't sound corporate." />
          </div>
        </div>
        <div className="mt-6 space-y-3">
          {[
            ["booking_enabled", "Allow booking appointments"],
            ["cancellation_enabled", "Allow cancellations"],
            ["rescheduling_enabled", "Allow rescheduling"],
            ["transfer_enabled", "Allow human transfer"],
          ].map(([k, l]) => (
            <div key={k} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <div className="text-sm font-medium">{l}</div>
              <Switch data-testid={`ai-toggle-${k}`} checked={!!ai[k]} onCheckedChange={(v) => set(k, v)} />
            </div>
          ))}
        </div>
        <Button data-testid="ai-save-btn" onClick={save} className="mt-6 rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button>
      </div>

      <Dialog open={warnOpen} onOpenChange={setWarnOpen}>
        <DialogContent data-testid="transfer-warning-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />No transfer number set</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            If someone calls with an emergency or urgently needs a human, there will be no way to connect them to you live.
            Instead, the receptionist will take their details, tell them a team member will call them back, and you'll be
            notified by email and SMS. You can always add the visit to your calendar manually.
          </p>
          <p className="text-sm text-slate-600">Add a transfer number above to have urgent calls ring your phone directly.</p>
          <div className="flex gap-2 pt-2">
            <Button data-testid="transfer-warning-save-anyway" onClick={doSave} variant="outline" className="flex-1 rounded-xl">Save anyway</Button>
            <Button data-testid="transfer-warning-add-number" onClick={() => setWarnOpen(false)} className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700">Add number</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
